import React from 'react';
import { tokens } from '../theme/tokens';
import { BRAND_NAME } from '../config/brand';
import { goFullReload } from '../utils/nav';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: 32 }}>
    <h2 style={{
      fontSize: 18, fontWeight: 600, color: tokens.colors.primary.neutral[100],
      margin: '0 0 12px', paddingBottom: 8,
      borderBottom: `1px solid ${tokens.colors.primary.neutral[800]}`
    }}>
      {title}
    </h2>
    {children}
  </div>
);

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ color: tokens.colors.primary.neutral[300], fontSize: 15, lineHeight: 1.75, margin: '0 0 10px' }}>
    {children}
  </p>
);

const Li: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li style={{ color: tokens.colors.primary.neutral[300], fontSize: 15, lineHeight: 1.75, marginBottom: 6 }}>
    {children}
  </li>
);

interface MetricCardProps {
  icon: string;
  name: string;
  value: string;
  desc: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ icon, name, value, desc }) => (
  <div style={{
    background: tokens.colors.primary.neutral[900],
    border: `1px solid ${tokens.colors.primary.neutral[800]}`,
    borderRadius: 12, padding: '18px 16px',
    display: 'flex', flexDirection: 'column', gap: 6,
  }}>
    <span style={{ fontSize: 22 }}>{icon}</span>
    <span style={{ fontSize: 13, color: tokens.colors.primary.neutral[400], fontWeight: 500 }}>{name}</span>
    <span className="luzu-gradient-text" style={{ fontSize: 22, fontWeight: 700 }}>{value}</span>
    <span style={{ fontSize: 12, color: tokens.colors.primary.neutral[500] }}>{desc}</span>
  </div>
);

interface ExampleRowProps {
  label: string;
  pts: string;
}

const ExampleRow: React.FC<ExampleRowProps> = ({ label, pts }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '8px 0', borderBottom: `1px solid rgba(255,255,255,0.05)`,
    fontSize: 14,
  }}>
    <span style={{ color: tokens.colors.primary.neutral[400] }}>{label}</span>
    <span style={{ color: tokens.colors.luzu.orange, fontWeight: 600 }}>{pts}</span>
  </div>
);

export const HowPointsWorkPage: React.FC = () => {
  return (
    <div style={{ background: tokens.colors.primary.neutral[1030], minHeight: '100vh' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '60px 24px 80px' }}>

        {/* Logo */}
        <div
          onClick={() => goFullReload('/')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 48 }}
        >
          <div style={{
            width: 36, height: 36, borderRadius: 9,
            background: tokens.colors.luzu.gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"
                 strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12.55a11 11 0 0 1 14.08 0"/>
              <path d="M1.42 9a16 16 0 0 1 21.16 0"/>
              <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
              <line x1="12" y1="20" x2="12.01" y2="20"/>
            </svg>
          </div>
          <span className="luzu-gradient-text" style={{ fontSize: 20, fontWeight: 700 }}>{BRAND_NAME}</span>
        </div>

        <h1 style={{ fontSize: 32, fontWeight: 700, color: tokens.colors.primary.neutral[50], margin: '0 0 8px' }}>
          Cómo funcionan los puntos
        </h1>
        <p style={{ color: tokens.colors.primary.neutral[400], fontSize: 16, marginBottom: 48 }}>
          Compartís contenido de {BRAND_NAME}, tu audiencia interactúa y vos acumulás puntos.
          Así se calcula cada punto que ganás.
        </p>

        {/* 1. Cómo se ganan */}
        <Section title="1. Cómo se ganan puntos">
          <P>
            Cada vez que publicás un contenido del catálogo a través de la app, medimos las
            vistas que genera en tu cuenta durante las{' '}
            <strong style={{ color: tokens.colors.primary.neutral[100] }}>primeras 72 horas</strong>{' '}
            desde la publicación. Los puntos se acreditan automáticamente.
          </P>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 12, margin: '20px 0'
          }}>
            <MetricCard icon="👁️" name="Vistas" value="1 pt" desc="por cada 2 vistas que recibe tu contenido" />
          </div>
          <div style={{
            background: tokens.colors.primary.neutral[900],
            borderLeft: `3px solid ${tokens.colors.luzu.orange}`,
            padding: '12px 16px', borderRadius: '0 8px 8px 0',
            fontSize: 14, color: tokens.colors.primary.neutral[400],
          }}>
            <strong style={{ color: tokens.colors.primary.neutral[100] }}>Nota:</strong>{' '}
            Por ahora solo las vistas suman puntos — los me gusta, comentarios, shares y guardados
            no se acreditan. Los valores pueden variar por campaña; siempre los vas a ver
            actualizados en la pantalla de detalle del contenido dentro de la app, antes de
            decidir si publicarlo.
          </div>
        </Section>

        {/* 2. Ejemplo */}
        <Section title="2. Ejemplo de cálculo">
          <P>Supongamos que publicás un video y en las primeras 72 hs obtiene:</P>
          <div style={{
            background: 'rgba(255,95,46,0.06)',
            border: '1px solid rgba(255,95,46,0.2)',
            borderRadius: 12, padding: 24, margin: '20px 0',
          }}>
            <h3 style={{ color: tokens.colors.luzu.orange, margin: '0 0 12px', fontSize: 15, fontWeight: 600 }}>
              📊 Video de ejemplo
            </h3>
            <ExampleRow label="12.000 vistas ÷ 2"  pts="6.000 pts" />
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              paddingTop: 14, fontSize: 16, fontWeight: 700,
            }}>
              <span style={{ color: tokens.colors.primary.neutral[50] }}>Total acreditado</span>
              <span className="luzu-gradient-text" style={{ fontSize: 20 }}>6.000 pts</span>
            </div>
          </div>
        </Section>

        {/* 3. Cuándo se acreditan */}
        <Section title="3. Cuándo se acreditan los puntos">
          <ul style={{ paddingLeft: 20 }}>
            <Li><strong style={{ color: tokens.colors.primary.neutral[100] }}>Publicación confirmada</strong> — publicás el contenido desde la app. Empieza el período de medición.</Li>
            <Li><strong style={{ color: tokens.colors.primary.neutral[100] }}>Ventana de 72 horas</strong> — medimos todas las interacciones generadas durante ese período.</Li>
            <Li><strong style={{ color: tokens.colors.primary.neutral[100] }}>Acreditación automática</strong> — a las 72 hs se calcula el total y los puntos se suman a tu saldo.</Li>
            <Li><strong style={{ color: tokens.colors.primary.neutral[100] }}>Notificación en la app</strong> — recibís un resumen de los puntos ganados por ese contenido.</Li>
          </ul>
        </Section>

        {/* 4. Multiplicadores */}
        <Section title="4. Bonificaciones y multiplicadores">
          <P>En determinadas campañas o períodos especiales podés obtener puntos extra:</P>
          <ul style={{ paddingLeft: 20 }}>
            <Li>
              <strong style={{ color: tokens.colors.primary.neutral[100] }}>Bonus de campaña</strong>{' '}
              — algunos contenidos incluyen un multiplicador especial indicado en la previsualización
              (ej. "×2 puntos este fin de semana").
            </Li>
            <Li>
              <strong style={{ color: tokens.colors.primary.neutral[100] }}>Primeras 24 hs ×1.5</strong>{' '}
              — las interacciones en las primeras 24 hs desde la publicación valen 1,5× más.
            </Li>
            <Li>
              <strong style={{ color: tokens.colors.primary.neutral[100] }}>Racha semanal +10%</strong>{' '}
              — si publicás al menos un contenido por semana durante 4 semanas seguidas, obtenés un 10%
              extra sobre todos los puntos de esa semana.
            </Li>
          </ul>
        </Section>

        {/* 5. Vencimiento */}
        <Section title="5. Vencimiento de puntos">
          <P>
            Los puntos vencen{' '}
            <strong style={{ color: tokens.colors.primary.neutral[100] }}>12 meses</strong>{' '}
            después de su acreditación. 30 días antes del vencimiento recibirás una notificación
            en la app. Los puntos vencidos no pueden recuperarse. Podés ver la fecha de vencimiento
            de cada lote en <em>Mi cuenta → Historial de puntos</em>.
          </P>
        </Section>

        {/* 6. Canje */}
        <Section title="6. Cómo canjear los puntos">
          <P>
            Una vez que acumulaste suficientes puntos, podés canjearlos por premios desde la sección
            <em> Tienda</em> dentro de la app. El canje está sujeto a:
          </P>
          <ul style={{ paddingLeft: 20 }}>
            <Li>Que tu cuenta esté activa y sin restricciones.</Li>
            <Li>Que haya stock disponible del premio elegido.</Li>
            <Li>Los premios no son canjeables por dinero en efectivo ni son transferibles.</Li>
          </ul>
        </Section>

        {/* FAQ */}
        <Section title="Preguntas frecuentes">
          <h3 style={{ fontSize: 15, fontWeight: 600, color: tokens.colors.primary.neutral[200], margin: '0 0 6px' }}>
            ¿Qué pasa si borro el video después de publicarlo?
          </h3>
          <P>
            Si eliminás el video antes de las 72 hs, los puntos se calculan hasta el momento de la
            eliminación. No se penalizan puntos ya acreditados, pero el período de medición cierra
            inmediatamente.
          </P>

          <h3 style={{ fontSize: 15, fontWeight: 600, color: tokens.colors.primary.neutral[200], margin: '20px 0 6px' }}>
            ¿El conteo de vistas incluye las propias?
          </h3>
          <P>
            No. Las plataformas (TikTok e Instagram) filtran automáticamente las vistas del propio creador.
            Solo contamos las vistas de terceros.
          </P>

          <h3 style={{ fontSize: 15, fontWeight: 600, color: tokens.colors.primary.neutral[200], margin: '20px 0 6px' }}>
            ¿Puedo ganar puntos con el mismo contenido en TikTok e Instagram al mismo tiempo?
          </h3>
          <P>
            Sí. Si tenés ambas cuentas conectadas y publicás en las dos plataformas, los puntos se
            calculan de forma independiente para cada una y se suman a tu saldo.
          </P>

          <h3 style={{ fontSize: 15, fontWeight: 600, color: tokens.colors.primary.neutral[200], margin: '20px 0 6px' }}>
            ¿Dónde veo el detalle de mis puntos?
          </h3>
          <P>
            En <em>Mi cuenta → Historial de puntos</em> encontrás el desglose completo: puntos por
            contenido, bonificaciones aplicadas, canjes realizados y fechas de vencimiento.
          </P>
        </Section>

        <p style={{ marginTop: 48, color: tokens.colors.primary.neutral[500], fontSize: 13 }}>
          Para más información revisá los{' '}
          <a href="/terms" style={{ color: tokens.colors.luzu.orange }}>Términos de Servicio</a>,
          Sección 5.{' '}
          ¿Tenés dudas? Escribinos a{' '}
          <a href="mailto:hola@drupi.io" style={{ color: tokens.colors.luzu.orange }}>hola@drupi.io</a>.
          <br /><br />
          © 2026 Drupi. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
};
